import { useState, useEffect } from 'react';
import AppBar from '@mui/material/AppBar';
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from 'react-router-dom';
import theme from '../../../theme';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ListItemIcon from '@mui/material/ListItemIcon';
import Typography from '@mui/material/Typography';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined';
import AirportShuttleOutlinedIcon from '@mui/icons-material/AirportShuttleOutlined';
import DiscountNotificationBanner from './DiscountNotificationBanner';
import { setLinkingTargetType } from "../../../store/actions/linkingTargetActions";

const pages = [
  {
    icon: <DirectionsCarOutlinedIcon />,
    text: 'Personenfahrzeug',
    linkingTargetType: 'P',
  },
  {
    icon: <AirportShuttleOutlinedIcon />,
    text: 'Lieferwagen',
    linkingTargetType: 'L',
  },
];

const getMenuButtonSx = (isActive: boolean) => ({
  my: 0,
  opacity: isActive ? 1 : 0.7,
  color: 'white',
  display: 'flex',
  borderRadius: '0',
  alignItems: 'center',
  fontSize: '12px',
  padding: '8px 12px',
  ...(isActive && { backgroundColor: theme.palette.secondary.main }),
  '&:hover': {
    opacity: 1,
    backgroundColor: theme.palette.secondary.main,
  },
});

const MainMenu = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [isHomeActive, setIsHomeActive] = useState(true);
  const [activeVehicleIndex, setActiveVehicleIndex] = useState<number | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const targetTypeParam = params.get('linkingTargetType');
    const vehicleIndex = pages.findIndex(
      (page) => page.linkingTargetType === targetTypeParam
    );

    if (vehicleIndex !== -1) {
      setActiveVehicleIndex(vehicleIndex);
      setIsHomeActive(false);
      return;
    }

    setActiveVehicleIndex(null);
    setIsHomeActive(location.pathname === '/');
  }, [location.pathname, location.search]);

  const handleHomeClick = () => {
    setIsHomeActive(true);
    setActiveVehicleIndex(null);
    navigate('/');
  };

  const handleVehicleClick = (index: number) => {
    setIsHomeActive(false);
    setActiveVehicleIndex(index);
    const newLinkingTargetType = pages[index].linkingTargetType;
    dispatch(setLinkingTargetType(newLinkingTargetType));
    navigate(`/?linkingTargetType=${newLinkingTargetType}`);
  };

  return (
    <AppBar position="static" sx={{ boxShadow: 'none', paddingTop: 1, paddingBottom: 1 }}>
      <Container
        maxWidth="xl"
        sx={{
          padding: '0px !important',
          marginBottom: { xs: '10px', sm: 0 },
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box sx={{ height: '30px', flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
          <Button sx={getMenuButtonSx(isHomeActive)} onClick={handleHomeClick}>
            <ListItemIcon sx={{ minWidth: 'auto', marginRight: '5px', color: 'white' }}>
              <HomeOutlinedIcon />
            </ListItemIcon>
            <Typography variant="inherit">Startseite</Typography>
          </Button>
          {pages.map((page, index) => (
            <Button
              key={page.linkingTargetType}
              sx={getMenuButtonSx(activeVehicleIndex === index)}
              onClick={() => handleVehicleClick(index)}
            >
              <ListItemIcon sx={{ minWidth: 'auto', marginRight: '5px', color: 'white' }}>
                {page.icon}
              </ListItemIcon>
              <Typography variant="inherit">{page.text}</Typography>
            </Button>
          ))}
        </Box>
        <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
          <DiscountNotificationBanner />
        </Box>
      </Container>
    </AppBar>
  );
};

export default MainMenu;
